// Browser-side text overlay compositor for the Static Ad (Pinterest).
// gpt-image-1 is unreliable at rendering clean readable text, so we generate
// a TEXT-FREE image and burn the Pinterest-hook headline onto it here using
// a 2:3 canvas. Output = a new dataURL ready to download or share.

const CANVAS_W = 1024;  // 2:3 portrait at gpt-image-1's native 1024x1536
const CANVAS_H = 1536;

// Load an image dataURL / remote URL into an <img> element.
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = src;
  });
}

// Word-wrap helper — returns an array of lines that fit within maxWidth.
function wrapLines(ctx, text, maxWidth) {
  const words = (text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width <= maxWidth) {
      line = test;
    } else {
      if (line) lines.push(line);
      line = w;
    }
  }
  if (line) lines.push(line);
  return lines;
}

// Composite the Pinterest overlay onto the generated image.
//   imageDataUrl     — raw image (gpt-image-1 output OR user-uploaded photo)
//   overlayText      — short Pinterest hook (8–14 words)
//   placement        — "top" | "bottom" | "left" | "right" (defaults to bottom)
//   supportingText   — optional secondary line (4–8 words)
//   colorTreatment   — "light" (white text on dark scrim, default) | "dark" (dark text on light scrim)
// Returns a dataURL (PNG) of the final composited ad.
export async function composeStaticAdOverlay({
  imageDataUrl,
  overlayText,
  placement = "bottom",
  supportingText = "",
  colorTreatment = "light",
}) {
  if (!imageDataUrl) throw new Error("Missing base image");
  const base = await loadImage(imageDataUrl);

  const canvas = document.createElement("canvas");
  canvas.width = CANVAS_W;
  canvas.height = CANVAS_H;
  const ctx = canvas.getContext("2d", { alpha: false });

  // 1. Draw image, cover-fit to 2:3 canvas.
  const iw = base.naturalWidth || base.width;
  const ih = base.naturalHeight || base.height;
  const scale = Math.max(CANVAS_W / iw, CANVAS_H / ih);
  const dw = iw * scale;
  const dh = ih * scale;
  const dx = (CANVAS_W - dw) / 2;
  const dy = (CANVAS_H - dh) / 2;
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
  ctx.drawImage(base, dx, dy, dw, dh);

  // Early exit if no overlay text.
  const text = (overlayText || "").trim();
  if (!text) return canvas.toDataURL("image/png");

  // 2. Compute the overlay band. Top/bottom use ~28% horizontal bands;
  //    left/right use ~42% vertical bands. The cleanest negative-space side
  //    is decided by Vision — we just render where it says.
  const horizontal = placement === "top" || placement === "bottom";
  const bandW = horizontal ? CANVAS_W : Math.round(CANVAS_W * 0.42);
  const bandH = horizontal ? Math.round(CANVAS_H * 0.28) : CANVAS_H;
  const bandX = placement === "right" ? CANVAS_W - bandW : 0;
  const bandY = placement === "bottom" ? CANVAS_H - bandH : 0;

  // 3. Gradient scrim direction depends on placement so it always fades
  //    AWAY from the photo's centre. Treatment "light" uses black scrim
  //    (white text); treatment "dark" uses white scrim (dark text).
  const scrimColor = colorTreatment === "dark" ? "255,255,255" : "0,0,0";
  const STRONG = `rgba(${scrimColor},0.78)`;
  const MID    = `rgba(${scrimColor},0.42)`;
  const ZERO   = `rgba(${scrimColor},0.0)`;
  let gx0, gy0, gx1, gy1, stops;
  if (placement === "top") {
    gx0 = 0; gy0 = 0;        gx1 = 0; gy1 = bandH;
    stops = [[0, STRONG], [0.6, MID], [1, ZERO]];
  } else if (placement === "bottom") {
    gx0 = 0; gy0 = bandY;    gx1 = 0; gy1 = CANVAS_H;
    stops = [[0, ZERO], [0.4, MID], [1, STRONG]];
  } else if (placement === "left") {
    gx0 = 0; gy0 = 0;        gx1 = bandW; gy1 = 0;
    stops = [[0, STRONG], [0.6, MID], [1, ZERO]];
  } else { // right
    gx0 = bandX; gy0 = 0;    gx1 = CANVAS_W; gy1 = 0;
    stops = [[0, ZERO], [0.4, MID], [1, STRONG]];
  }
  const g = ctx.createLinearGradient(gx0, gy0, gx1, gy1);
  stops.forEach(([o, c]) => g.addColorStop(o, c));
  ctx.fillStyle = g;
  ctx.fillRect(bandX, bandY, bandW, bandH);

  // 4. Wrap + draw the headline + optional supporting line.
  // Side bands wrap into a narrower box than top/bottom bands.
  const sidePad = horizontal ? 80 : 56;
  const maxWidth = (horizontal ? CANVAS_W : bandW) - sidePad * 2;
  const maxLines = horizontal ? 3 : 5;
  const fontFamily = '"DM Serif Display", "Playfair Display", "Georgia", serif';

  let fontSize = horizontal ? 96 : 72;
  let lines = [];
  while (fontSize >= 40) {
    ctx.font = `700 ${fontSize}px ${fontFamily}`;
    lines = wrapLines(ctx, text, maxWidth);
    if (lines.length <= maxLines) break;
    fontSize -= 6;
  }

  const lineHeight = Math.round(fontSize * 1.12);
  const totalTextH = lineHeight * lines.length;

  // Vertical anchor: top → near top of band; bottom → near bottom of band;
  // side → centred vertically in band.
  let startY;
  if (placement === "top") {
    startY = Math.round(bandH * 0.22) + fontSize;
  } else if (placement === "bottom") {
    startY = CANVAS_H - Math.round(bandH * 0.30) - totalTextH + fontSize;
  } else {
    startY = (CANVAS_H - totalTextH) / 2 + fontSize;
  }

  // Horizontal anchor: top/bottom left-aligned at sidePad; left placement at
  // band's left edge; right placement at band's right edge (right-aligned).
  const rightAligned = placement === "right";
  ctx.textAlign = rightAligned ? "right" : "left";
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = colorTreatment === "dark" ? "#0b0b0e" : "#ffffff";
  ctx.shadowColor = colorTreatment === "dark"
    ? "rgba(255,255,255,0.35)"
    : "rgba(0,0,0,0.55)";
  ctx.shadowBlur = 14;
  ctx.shadowOffsetY = 2;

  const textX = rightAligned
    ? bandX + bandW - sidePad
    : bandX + sidePad;

  lines.forEach((ln, i) => {
    ctx.fillText(ln, textX, startY + i * lineHeight);
  });

  // 5. Optional supporting sub-line (smaller, ~45% of headline font size).
  const supText = (supportingText || "").trim();
  if (supText) {
    const subSize = Math.max(24, Math.round(fontSize * 0.42));
    ctx.font = `400 ${subSize}px "Inter", "Helvetica Neue", Arial, sans-serif`;
    const subY = startY + (lines.length - 1) * lineHeight + Math.round(subSize * 1.8);
    ctx.fillText(supText, textX, subY);
  }

  // Reset shadow state.
  ctx.shadowColor = "transparent";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;

  return canvas.toDataURL("image/png");
}
